import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Skeleton,
  useMediaQuery,
  Tooltip,
  Badge,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faChartLine,
  faCompactDisc,
  faBuildingUser,
  faLaptopCode,
  faCog,
  faArchive,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import useAdminStatus from "../../Hooks/useAdminStatus";
import useDeployments from "../../Hooks/useDeployments"; 
import useAuth from "../../Hooks/useAuth";

function MenuBar({ isActive }) {
  const theme = useTheme();
  const { isAdmin, loading } = useAdminStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();

  // Get deployment data including completed count
  const { completedDeploymentsCount } = useDeployments(user, false);

  const activeColor = theme.palette.success.main;

  const menuItems = useMemo(
    () => [
      { text: "Deployments", icon: faLaptopCode, path: "/",  adminOnly: false },
      { text: "Archived", icon: faArchive, path: "/archived-deployments", adminOnly: false },
      { text: "Apps", icon: faCompactDisc, path: "/application-management", adminOnly: false },
      { text: "Companies", icon: faBuildingUser, path: "/company-management", adminOnly: false },
      // { text: "Devices", icon: faBuildingUser, path: "/device-management", adminOnly: true },
      {
        text: "Logs",
        icon: faFileAlt,
        path: "/deployment-logs",
        adminOnly: true,
      },
      {
        text: "Statistics",
        icon: faChartLine,
        path: "/statistics",
        adminOnly: true,
      },
      { text: "Settings", icon: faCog, path: "/settings", adminOnly: true },
      { text: "FAQ's", icon: faQuestionCircle, path: "/kb", adminOnly: false },
    ],
    []
  );

  // Focus management
  const [focusIndex, setFocusIndex] = useState(0);
  const menuItemRefs = useRef([]); 

  useEffect(() => {
    if (isActive) {
      setFocusIndex(0); // Reset focus index when menu becomes active
    }
  }, [isActive]);

  const handleKeyDown = (event) => {
    if (!isActive) return;

    switch (event.key) {
      case "ArrowRight":
        setFocusIndex((prevIndex) =>
          prevIndex < menuItems.length - 1 ? prevIndex + 1 : 0
        );
        break;
      case "ArrowLeft":
        setFocusIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : menuItems.length - 1
        );
        break;
      case "Enter":
        if (menuItems[focusIndex]) {
          navigate(menuItems[focusIndex].path);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isActive && menuItemRefs.current[focusIndex]) {
      menuItemRefs.current[focusIndex].focus();
    }
  }, [focusIndex, isActive]);

  const filteredMenuItems = useMemo(() => {
    return menuItems.filter((item) => !item.adminOnly || isAdmin);
  }, [menuItems, isAdmin]);

  // Conditional rendering of the menu
  if (!isActive) {
    return null; // Do not render the menu if it's not active
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        padding: isSmallScreen ? "6px 10px" : "10px 20px",
        display: "flex",
        justifyContent: "space-around",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
        top: "80px",
        overflowX: "auto", // Enable horizontal scrolling on smaller screens
        height: isSmallScreen ? "48px" : "auto",
        alignItems: "center",
        scrollbarWidth: "none", // Hide scrollbar for Firefox
        "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar for Chrome, Safari, and Opera
      }}
      tabIndex="0"
      onKeyDown={handleKeyDown}
    >
      {loading ? (
        <>
          {Array.from(new Array(6)).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width={isSmallScreen ? 40 : 80}
              height={40}
              sx={{ margin: "0 8px", borderRadius: "4px" }}
            />
          ))}
        </>
      ) : (
        filteredMenuItems.map((item, index) => (
          <Tooltip
            key={item.text}
            title={isSmallScreen ? item.text : ""}
            arrow
          >
            <MenuItem
              component={Link}
              to={item.path}
              disabled={location.pathname === item.path}
              sx={{
                flexDirection: isSmallScreen ? "column" : "row",
                color:
                  location.pathname === item.path
                    ? activeColor
                    : theme.palette.info.main,
                backgroundColor:
                  location.pathname === item.path
                    ? theme.palette.success.text
                    : "inherit",
                cursor: location.pathname === item.path ? "default" : "pointer",
                borderRadius: "8px",
                margin: "0 8px",
                minWidth: isSmallScreen ? "40px" : "auto",
                padding: isSmallScreen ? "4px 4px" : "8px 16px",
                justifyContent: "center",
              }}
              ref={(el) => (menuItemRefs.current[index] = el)}
              autoFocus={focusIndex === index}
              onFocus={() => setFocusIndex(index)}
            >
              <ListItemIcon
                sx={{
                  minWidth: "auto",
                  justifyContent: "center",
                  mr: isSmallScreen ? 0 : 1,
                }}
              >
                {item.text === "Deployments" ? (
                  <Badge
                    badgeContent={completedDeploymentsCount}
                    color="success"
                  >
                    <FontAwesomeIcon
                      icon={item.icon}
                      style={{
                        color:
                          location.pathname === item.path
                            ? activeColor
                            : "#FDFEFE",
                      }}
                    />
                  </Badge>
                ) : (
                  <FontAwesomeIcon
                    icon={item.icon}
                    style={{
                      color:
                        location.pathname === item.path
                          ? activeColor
                          : "#FDFEFE",
                    }}
                  />
                )}
              </ListItemIcon>
              {!isSmallScreen && (
                <ListItemText primary={item.text} sx={{ ml: 1 }} />
              )}
            </MenuItem>
          </Tooltip>
        ))
      )}
    </Box>
  );
}

export default MenuBar;
